import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  partnerCostsInitialState,
  initialBusPartnerSpecificCostData,
  busPartnerCostData
} from './data';
import {
  BusPartnerCostYear,
  PartnerCostEntryYear,
  PartnerCostsVehicleCost,
  PartnerCostsBusTypeCost,
  PartnerCostsDriverCost
} from './types';

const partnerCostsSlice = createSlice({
  name: 'partner-costs',
  initialState: partnerCostsInitialState,
  reducers: {
    busPartnersCostYearsFetched(
      state,
      {payload}: PayloadAction<BusPartnerCostYear[]>
    ) {
      state.busPartnersCostYears = payload;
    },
    qualityCheckedBusPartnerCostEntryYear(
      state,
      {payload: {qualityChecked}}: PayloadAction<{qualityChecked: boolean}>
    ) {
      return {
        ...state,
        qualityChecked
      };
    },
    resetBusPartnerSpecificData(state) {
      return {
        ...state,
        ...initialBusPartnerSpecificCostData
      };
    },
    receivedBusPartnerCostEntryYear(state, {payload}: PayloadAction<any>) {
      const newState = {
        ...state,
        qualityChecked: payload.qualityChecked,
        vehicleCost: payload?.busCostEntry,
        vehicleTypeCosts: {
          ...state.vehicleTypeCosts,
          list: payload?.busTypeCostEntries
        },
        driverCost: payload?.driverCostEntry
      };

      newState.countryCost = {
        vehicleCost: payload?.countryCostEntry?.busCostEntry,
        vehicleTypeCosts: payload?.countryCostEntry?.busTypeCostEntries,
        driverCost: payload?.countryCostEntry?.driverCostEntry
      };

      return newState;
    },
    addedNewYear: (
      state,
      {payload}: PayloadAction<PartnerCostEntryYear & {busPartnerId: string}>
    ) => {
      const {
        busPartnerId,
        year,
        busCostEntry,
        busTypeCostEntries,
        driverCostEntry,
        qualityChecked
      } = payload;

      const busPartnersCostYears =
        state.busPartnersCostYears?.map(busPartnerCostYears => {
          if (busPartnerCostYears.busPartnerId === busPartnerId) {
            return {
              busPartnerId: busPartnerCostYears.busPartnerId,
              years: [...busPartnerCostYears.years, year]
            };
          }
          return busPartnerCostYears;
        }) ?? null;
      return {
        ...state,
        ...busPartnerCostData,
        busPartnersCostYears,
        vehicleCost: busCostEntry,
        vehicleTypeCosts: {
          list: busTypeCostEntries,
          vehicleTypeCost: null
        },
        driverCost: driverCostEntry,
        qualityChecked
      };
    },
    deletedNewYear(
      state,
      {payload}: PayloadAction<{busPartnerId: string; year: number}>
    ) {
      const {busPartnerId, year} = payload;
      const busPartnersCostYears =
        state.busPartnersCostYears?.map(busPartnerCostYear => {
          if (busPartnerCostYear.busPartnerId === busPartnerId) {
            return {
              busPartnerId: busPartnerCostYear.busPartnerId,
              years: busPartnerCostYear.years.filter(y => y !== year)
            };
          }
          return busPartnerCostYear;
        }) ?? null;

      return {
        ...state,
        ...busPartnerCostData,
        busPartnersCostYears
      };
    },

    // Driver cost
    receivedPartnerDriverCost(
      state,
      {payload}: PayloadAction<PartnerCostsDriverCost>
    ) {
      return {
        ...state,
        driverCost: payload
      };
    },

    // Vehicle cost
    receivedPartnerVehicleCostAction(
      state,
      {payload}: PayloadAction<PartnerCostsVehicleCost>
    ) {
      return {
        ...state,
        vehicleCost: payload
      };
    },
    addToVehicleTypeCostAction(
      state,
      {payload}: PayloadAction<PartnerCostsBusTypeCost>
    ) {
      const list = [...(state.vehicleTypeCosts?.list ?? []), payload];

      return {
        ...state,
        vehicleTypeCosts: {
          ...state.vehicleTypeCosts,
          list
        }
      };
    },
    updateVehicleTypeCostAction(
      state,
      {payload}: PayloadAction<PartnerCostsBusTypeCost>
    ) {
      const list =
        state.vehicleTypeCosts?.list?.map(vehicleTypeCost => {
          return payload.id === vehicleTypeCost?.id ? payload : vehicleTypeCost;
        }) ?? [];

      return {
        ...state,
        vehicleTypeCosts: {
          ...state.vehicleTypeCosts,
          list
        }
      };
    },
    deleteFromVehicleTypeCostAction(
      state,
      {payload}: PayloadAction<PartnerCostsBusTypeCost>
    ) {
      const list =
        state.vehicleTypeCosts?.list?.filter(
          vehicleTypeCost => payload.id !== vehicleTypeCost?.id
        ) ?? [];

      return {
        ...state,
        vehicleTypeCosts: {
          ...state.vehicleTypeCosts,
          list
        }
      };
    }
  }
});

export const {
  busPartnersCostYearsFetched,
  qualityCheckedBusPartnerCostEntryYear,
  resetBusPartnerSpecificData,
  receivedBusPartnerCostEntryYear,
  addedNewYear,
  deletedNewYear,

  // Driver cost
  receivedPartnerDriverCost,

  // Vehicle cost
  receivedPartnerVehicleCostAction,
  addToVehicleTypeCostAction,
  updateVehicleTypeCostAction,
  deleteFromVehicleTypeCostAction
} = partnerCostsSlice.actions;

export default partnerCostsSlice.reducer;
